// SheetletElement.js
import React, { useEffect, useState } from 'react'
import SheetletPlaceholder from './Sheetlet-placeholder.png' // Import the PNG file
import { useDispatch, useSelector } from 'react-redux'
import {
    updateSheetletPosition,
    updateSheetletSize,
    updateSheetletTitle,
} from '../../../store/actions'
import SheetletEdit from './SheetletEdit'
import { ServerContext } from '../../screens/Source/SheetState'
import Sheetlet from '../../screens/Source/Sheetlet/Sheetlet'
import { Server } from '../../../api/Server'
import { ElementWrapper } from '../components/ElementWrapper/ElementWrapper'
import { SquaresFourIcon, LinkIcon, LinkBreakIcon } from '@phosphor-icons/react'
import { renderToString } from 'react-dom/server'
import { OBJECT_TYPES } from '../types'
import { togglePromptObject } from '../../../store/actions/promptActions'
import { ElementInfo } from '../components/ElementInfo/ElementInfo'
import { ScoopTextField, ScoopButton } from '../../ui'
import styles from './SheetletElement.module.scss'

const SheetletElement = ({
    id,
    title,
    initialPosition,
    initialSize,
    content,
    shouldHideGrid,
    shouldHideHeaders,
    presentationScale,
    workspaceID,
    userID,
    token,
    multiProps,
    inboxes,
}) => {
    const sheetletId = `SheetletElement-${id}`
    const worksheetID = content.worksheetID
    const worksheetRange = content.worksheetRange
    const worksheetNum = content.worksheetNum
    const worksheetName = content.worksheetName
    const worksheetURL = content.worksheetURL
    const [server, setServer] = React.useState(new Server(workspaceID, userID, token))
    const dispatch = useDispatch()
    const [isSheetletASkeleton, setIsSheetletASkeleton] = React.useState(true) //when there is no Sheetlet data to display
    const [inputTitle, setInputTitle] = useState(title) // Local state for the input title
    const [isEditMode, setIsEditMode] = useState(false) // Local state for the edit mode
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [editSheetletOpen, setEditSheetletOpen] = useState(false)
    const [serverContext, setServerContext] = useState(new ServerContext(server))
    const [sheetletContent, setSheetletContent] = useState(null)
    const [wrapperSize, setWrapperSize] = useState(initialSize)
    const [isGridVisible, setIsGridVisible] = useState(!shouldHideGrid || true)
    const [isHeadersVisible, setIsHeadersVisible] = useState(!shouldHideHeaders || true)
    const promptSelectingObjects = useSelector((state) => state.prompt.promptSelectingObjects)
    const promptSelecting = useSelector((state) => state.prompt.promptSelecting)
    const objects = useSelector((state) => state.objects)
    const isGuestMode = useSelector((state) => state.auth.isGuestMode)
    const guestPrompts = useSelector((state) => state.auth.guestPrompts)
    const updatedStyles = useSelector(
        (state) => state.objects.find((obj) => obj.id === id)?.wrapperStyles || {}
    )
    const activeMode = useSelector((state) => state.ui.activeMode)

    const getSheetletPrompts = () => {
        let prompts
        if (isGuestMode && guestPrompts.length > 0) prompts = guestPrompts
        else prompts = objects.filter((obj) => obj.type === OBJECT_TYPES.PROMPT)
        return prompts
            .filter((prompt) => prompt.promptProps?.objects.includes(sheetletId))
            .map((prompt) => {
                let result = prompt.promptProps.prompt
                if (prompt.promptProps.refreshInputQueries) {
                    result.refreshInputQueries = true
                }
                return result
            })
            .filter((prompt) => prompt !== null)
    }

    useEffect(() => {
        setIsGridVisible(!shouldHideGrid)
        setIsHeadersVisible(!shouldHideHeaders)
    }, [shouldHideGrid, shouldHideHeaders])

    useEffect(() => {
        if (worksheetID && (worksheetNum !== null || worksheetName !== null || worksheetRange)) {
            // console.log("worksheetRange,worksheetNum, worksheetName ,worksheetURL:",worksheetRange,worksheetNum, worksheetName ,worksheetURL )
            if (!worksheetRange) {
                setSheetletContent({
                    sheetType: 'LiveSheet',
                    worksheetID: worksheetID,
                    sheetNum: worksheetNum,
                    sheetName: worksheetName || null,
                })
            } else {
                setSheetletContent({
                    sheetType: 'LiveSheet',
                    worksheetID: worksheetID,
                    rangeName: worksheetRange,
                })
            }
            setIsSheetletASkeleton(false)
        }
    }, [worksheetID, worksheetNum, worksheetName, worksheetRange])

    useEffect(() => {
        setInputTitle(title)
    }, [title])

    const handleTitleChange = (e) => {
        setInputTitle(e.target.value)
        dispatch(updateSheetletTitle(id, e.target.value)) // Dispatch the UPDATE_SHEETLET_TITLE event
    }

    React.useEffect(() => {
        setServer(new Server(workspaceID, userID, token))
    }, [userID, token, workspaceID])

    // Gabe commented this out -- this nullifies serverContext.serverData
    React.useEffect(() => {
        // Use the functional form of setState to ensure the latest server value is used
        // setServerContext(prevServerContext => new ServerContext(prevServerContext.server));
    }, [server])

    const updateSheetletPos = (position) => {
        dispatch(updateSheetletPosition(id, position))
    }

    const updateSheetletSz = (size) => {
        dispatch(updateSheetletSize(id, size))
    }

    const promptSelected = promptSelectingObjects.includes(sheetletId)

    return (
        <ElementWrapper
            id={sheetletId}
            key={sheetletId}
            editMode={isEditMode}
            toggleEditMode={setIsEditMode}
            initialSize={initialSize}
            initialPosition={initialPosition}
            updateElementPosition={updateSheetletPos}
            updateElementSize={updateSheetletSz}
            setWrapperSizeTrack={setWrapperSize}
            type={'sheetlet'}
            {...multiProps}
            objectId={id}
        >
            {promptSelecting && (
                <div className={styles.promptLinkContainer}>
                    <ScoopButton
                        variant="text"
                        className={`prompt-link-button ${promptSelected ? styles.promptLinkSelected : styles.promptLinkUnselected}`}
                        onClick={() => dispatch(togglePromptObject(sheetletId))}
                    >
                        {promptSelected ? <LinkIcon size={20} /> : <LinkBreakIcon size={20} />}
                    </ScoopButton>
                </div>
            )}
            {(isEditMode || activeMode === 'cursor') && (
                <>
                    <div className={styles.editMenuContainer}>
                        <SheetletEdit
                            isMenuOpen={isMenuOpen}
                            setIsMenuOpen={setIsMenuOpen}
                            isSheetletASkeleton={isSheetletASkeleton}
                            setIsSheetletASkeleton={setIsSheetletASkeleton}
                            server={server}
                            title={title}
                            id={id}
                            worksheetID={worksheetID}
                            worksheetRange={worksheetRange}
                            worksheetNum={worksheetNum}
                            worksheetName={worksheetName}
                            worksheetURL={worksheetURL}
                            handleTitleChange={handleTitleChange}
                            editSheetletOpen={editSheetletOpen}
                            setEditSheetletOpen={setEditSheetletOpen}
                            shouldHideGrid={!isGridVisible}
                            shouldHideHeaders={!isHeadersVisible}
                            inboxes={inboxes}
                            isCursorMode={activeMode === 'cursor'}
                            isEditMode={isEditMode}
                        />
                    </div>
                    {isEditMode && activeMode === 'edit' && (
                        <ElementInfo id={id} type={'Sheetlet'} title={title ?? 'Untitled'} />
                    )}
                </>
            )}
            {updatedStyles.showTitle && (
                <div className={styles.titleContainer}>
                    <ScoopTextField
                        placeholder={title}
                        aria-label="description"
                        value={inputTitle}
                        onChange={handleTitleChange}
                        disableMargin
                    />
                </div>
            )}
            {isSheetletASkeleton && (
                <div
                    className={styles.skeletonPlaceholder}
                    style={{ backgroundImage: `url(${SheetletPlaceholder})` }}
                />
            )}
            {!isSheetletASkeleton && (
                <div
                    className={styles.sheetletContainer}
                    style={{ height: updatedStyles.showTitle ? 'calc(100% - 40px)' : '100%' }}
                >
                    <Sheetlet
                        presentationScale={presentationScale}
                        sheetletId={sheetletId}
                        serverContext={serverContext}
                        sheetRange={sheetletContent}
                        embeddedSizeProps={{
                            left: 0,
                            height: updatedStyles.showTitle ? wrapperSize.height - 40 : '100%',
                        }}
                        shouldHideGrid={!isGridVisible}
                        shouldHideHeaders={!isHeadersVisible}
                        activePrompts={getSheetletPrompts()}
                        cornerHTML={
                            '<a href="https://docs.google.com/spreadsheets/d/' +
                            worksheetID +
                            '" target="_blank">' +
                            renderToString(<SquaresFourIcon size={20} />) +
                            '</a>'
                        }
                    />
                </div>
            )}
        </ElementWrapper>
    )
}

export default SheetletElement
